import { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

export interface DebouncedCallback<Args extends unknown[]> {
  // schedule the callback to run once delayMs has elapsed without another call
  call: (...args: Args) => void;
  // run the scheduled call right now (no-op when nothing is scheduled)
  flush: () => void;
  // drop the scheduled call without running it
  cancel: () => void;
}

// Collapses a burst of calls into a single trailing call carrying the arguments of the most recent one.
// Kept free of React so the timing behavior can be unit tested on its own.
export function createDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs: number,
): DebouncedCallback<Args> {
  let timeout: ReturnType<typeof setTimeout> | undefined;
  let pendingArgs: Args | undefined;

  function clearTimer() {
    if (timeout === undefined) {
      return;
    }
    clearTimeout(timeout);
    timeout = undefined;
  }

  function runPending() {
    if (!pendingArgs) {
      return;
    }
    const args = pendingArgs;
    // clear before invoking so a flush triggered from within the callback cannot run it twice
    pendingArgs = undefined;
    callback(...args);
  }

  return {
    call: (...args: Args) => {
      pendingArgs = args;
      clearTimer();
      timeout = setTimeout(() => {
        timeout = undefined;
        runPending();
      }, delayMs);
    },
    flush: () => {
      clearTimer();
      runPending();
    },
    cancel: () => {
      clearTimer();
      pendingArgs = undefined;
    },
  };
}

/**
 * Debounces `callback`, collapsing a burst of calls into a single trailing call with the latest arguments.
 *
 * Intended for deferring writes, so a scheduled call is never silently dropped: it is flushed when the
 * component unmounts (or when `delayMs` changes), and callers can flush it themselves — e.g. when the
 * record being edited changes, or when the page is hidden.
 *
 * The scheduled call always runs the callback from the most recent render, so a deferred write cannot
 * persist values captured in a stale closure.
 */
export function useDebouncedCallback<Args extends unknown[]>(
  callback: (...args: Args) => void,
  delayMs: number,
): DebouncedCallback<Args> {
  const callbackRef = useRef(callback);

  // a layout effect (rather than a passive one) so the ref is refreshed before the passive effect
  // cleanup below flushes a pending call, which happens in the same commit as a song change
  useLayoutEffect(() => {
    callbackRef.current = callback;
  });

  const debounced = useMemo(
    () => createDebouncedCallback<Args>((...args) => callbackRef.current(...args), delayMs),
    [delayMs],
  );

  useEffect(() => () => debounced.flush(), [debounced]);

  return debounced;
}
